import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useRef, useEffect } from 'react';
import DoramaCard from '../components/DoramaCard';
import SkeletonCard from '../components/SkeletonCard';
import TelegramBanner from '../components/TelegramBanner';
import HeroCarousel from '../components/HeroCarousel';
import LeaderboardWidget from '../components/LeaderboardWidget';
import { useGetMaterialsQuery } from '../services/materialsApi';
import { getPosterUrl } from '../utils/poster';
import styles from '../css/Home.module.css';

const DRAG_THRESHOLD = 8;
const OVERSHOOT_LIMIT = 90; // max px past edge (visual)

// Hyperbolic compression: maps [0..∞) → [0..LIMIT)
// At t=LIMIT input → LIMIT/2 output (feels like half resistance)
const compress = (t, limit) => limit * t / (t + limit);

// Logical → visual: apply soft wall outside [max..0]
const rubberize = (logical, max) => {
  if (logical > 0)   return  compress(logical, OVERSHOOT_LIMIT);
  if (logical < max) return max - compress(max - logical, OVERSHOOT_LIMIT);
  return logical;
};

const useDragScroll = () => {
  const viewportRef = useRef(null);
  const trackRef    = useRef(null);
  const s = useRef({
    active:    false,
    didDrag:   false,
    startPtr:  0,
    startY:    0,     // для определения направления жеста (touch)
    axisLocked: null, // 'x' | 'y' | null — направление жеста определено по первым пикселям
    logical:   0,   // unbounded logical position (what physics works on)
    visual:    0,   // rubberized visual position (what is drawn)
    vel:       0,   // px/ms
    lastPtr:   0,
    lastT:     0,
    raf:       null,
  });

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const st = s.current;

    const getMax = () => {
      const t = trackRef.current;
      if (!t) return 0;
      return -(t.scrollWidth - vp.clientWidth);
    };

    const draw = (visual) => {
      if (trackRef.current)
        trackRef.current.style.transform = `translate3d(${visual}px,0px,0px)`;
    };

    // TouchEvent не имеет clientX/clientY — координаты берём из touches[0]
    const getX = (e) => (e.touches ? e.touches[0].clientX : e.clientX);
    const getY = (e) => (e.touches ? e.touches[0].clientY : e.clientY);

    // ── drag ───────────────────────────────────────────────────────────────
    const onDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      const x = getX(e);
      cancelAnimationFrame(st.raf);
      Object.assign(st, {
        active:     true,
        didDrag:    false,
        startPtr:   x,
        startY:     getY(e),
        axisLocked: null,
        logical:    st.logical,
        vel:        0,
        lastPtr:    x,
        lastT:      performance.now(),
      });
      st._startX = x - st.logical;
    };

    const onMove = (e) => {
      if (!st.active) return;
      const x = getX(e);

      // Для touch — определяем направление жеста по первым пикселям:
      // горизонтальный ведёт карусель (блокируем скролл страницы),
      // вертикальный отдаём браузеру для обычного скролла.
      if (e.touches && st.axisLocked === null) {
        const dx = Math.abs(x - st.startPtr);
        const dy = Math.abs(getY(e) - st.startY);
        if (dx < DRAG_THRESHOLD && dy < DRAG_THRESHOLD) return;
        st.axisLocked = dx > dy ? 'x' : 'y';
        if (st.axisLocked === 'y') {
          st.active = false;
          return;
        }
      }
      if (st.axisLocked === 'y') return;
      if (e.cancelable) e.preventDefault();

      const now = performance.now();
      const dt  = now - st.lastT;

      if (dt > 0) {
        const raw = (x - st.lastPtr) / dt;
        const w = Math.min(dt / 16, 1);
        st.vel = st.vel * (1 - w * 0.55) + raw * (w * 0.55);
      }
      st.lastPtr = x;
      st.lastT   = now;

      if (Math.abs(x - st.startPtr) > DRAG_THRESHOLD)
        st.didDrag = true;

      st.logical = x - st._startX;
      st.visual  = rubberize(st.logical, getMax());
      draw(st.visual);
    };

    // ── release ────────────────────────────────────────────────────────────
    const onUp = (e) => {
      if (!st.active) return;
      st.active = false;

      // Block the upcoming click only if user actually dragged
      if (st.didDrag) {
        const absorb = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        window.addEventListener('click', absorb, { capture: true, once: true });
      }

      let vel = Math.sign(st.vel) * Math.min(Math.abs(st.vel), 1.8);
      let logical = st.logical;
      let prevT   = 0;

      const tick = (ts) => {
        const dt  = prevT ? Math.min(ts - prevT, 64) : 16;
        prevT = ts;
        const max = getMax();

        const outLeft  = logical > 0;
        const outRight = logical < max;
        const out      = outLeft || outRight;

        const halfLife = out ? 45 : 400;
        vel *= Math.exp(-Math.LN2 / halfLife * dt);
        logical += vel * dt;

        const visual = rubberize(logical, max);
        draw(visual);
        st.logical = logical;
        st.visual  = visual;

        if (out && Math.abs(vel) < 0.15) {
          const target = outLeft ? 0 : max;
          const alpha  = 1 - Math.exp(-Math.LN2 / 90 * dt);
          logical += (target - logical) * alpha;

          const v2 = rubberize(logical, max);
          draw(v2);
          st.logical = logical;
          st.visual  = v2;

          if (Math.abs(target - logical) < 0.15) {
            draw(target); st.logical = target; st.visual = target; return;
          }
          st.raf = requestAnimationFrame(tick);
          return;
        }

        if (!out && Math.abs(vel) < 0.008) return;

        st.raf = requestAnimationFrame(tick);
      };

      st.raf = requestAnimationFrame(tick);
    };

    const onDragStart = (e) => e.preventDefault();

    vp.addEventListener('mousedown',  onDown);
    vp.addEventListener('mousemove',  onMove);
    vp.addEventListener('mouseup',    onUp);
    vp.addEventListener('mouseleave', onUp);
    vp.addEventListener('touchstart', onDown, { passive: true });
    vp.addEventListener('touchmove',  onMove, { passive: false });
    vp.addEventListener('touchend',   onUp);
    vp.addEventListener('dragstart',  onDragStart);

    return () => {
      cancelAnimationFrame(st.raf);
      vp.removeEventListener('mousedown',  onDown);
      vp.removeEventListener('mousemove',  onMove);
      vp.removeEventListener('mouseup',    onUp);
      vp.removeEventListener('mouseleave', onUp);
      vp.removeEventListener('touchstart', onDown);
      vp.removeEventListener('touchmove',  onMove);
      vp.removeEventListener('touchend',   onUp);
      vp.removeEventListener('dragstart',  onDragStart);
    };
  }, []);

  return { viewportRef, trackRef };
};

const CardRow = ({ isLoading, items, skeletonCount = 12 }) => {
  const { viewportRef, trackRef } = useDragScroll();

  return (
    <div className={styles.carouselViewport} ref={viewportRef}>
      <div className={styles.carouselTrack} ref={trackRef}>
        {isLoading
          ? Array.from({ length: skeletonCount }, (_, i) => <SkeletonCard key={i} />)
          : items.map((item) => (
              <DoramaCard
                key={item.id}
                id={item.id}
                slug={item.slug}
                slug_id={item.slug_id}
                image={getPosterUrl(item.poster_url)}
                title={item.title}
                rating={item.imdb_rating ?? item.mydramalist_rating ?? item.kinopoisk_rating}
                year={item.year}
                genres={item.genres ?? []}
                status={item.drama_status}
              />
            ))
        }
      </div>
    </div>
  );
};

const Home = () => {
  const { data: new2026Data, isLoading: isNew2026Loading } = useGetMaterialsQuery({
    year: 2026,
    sort_by: 'imdb_rating',
    per_page: 12,
  });

  const { data: top100Data, isLoading: isTop100Loading } = useGetMaterialsQuery({
    sort_by: 'top100',
    per_page: 12,
    page: 3,
  });

  const new2026 = new2026Data?.data ?? [];
  const top100 = top100Data?.data ?? [];

  // Прогреваем чанки часто посещаемых страниц в простое браузера, чтобы
  // переход на них после захода на главную не показывал RouteFallback.
  useEffect(() => {
    const prefetch = () => {
      import('./Catalog');
      import('./Top100');
      import('./New');
      import('./Ongoing');
      import('./Bookmarks');
      import('./Profile');
      import('./Watch');
      import('./History');
      import('./Settings');
      import('./Lakorns');
      import('./TopUsers');
      import('./UserProfile');
      import('./Notifications');
    };
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(prefetch, { timeout: 3000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(prefetch, 2000);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className={styles.homePage}>
      <Helmet>
        <title>DoramaHype — дорамы с русской озвучкой онлайн</title>
        <meta name="description" content="Смотрите лучшие корейские дорамы, дорамы 2026, Топ 100 дорам с русской озвучкой бесплатно онлайн на DoramaHype." />
        <meta property="og:title" content="DoramaHype — дорамы с русской озвучкой онлайн" />
        <meta property="og:description" content="Смотрите лучшие корейские дорамы, дорамы 2026, Топ 100 дорам с русской озвучкой бесплатно онлайн на DoramaHype." />
        <meta property="og:url" content="https://doramahype.ru/" />
      </Helmet>

      <HeroCarousel />
      <TelegramBanner />
      <LeaderboardWidget
        title="Топ месяца"
        period="current_month"
        discipline="minutes"
        linkTo="/top-users"
      />

      {/* Новинки 2026 */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Новинки 2026</h2>
          <Link to="/new" className={styles.viewAll}>
            Все
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
        <CardRow isLoading={isNew2026Loading} items={new2026} />
      </div>

      {/* Топ 100 */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Топ 100</h2>
          <Link to="/top-100" className={styles.viewAll}>
            Все
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
        <CardRow isLoading={isTop100Loading} items={top100} />
      </div>
    </div>
  );
};

export default Home;
